import './styles.scss';

import React, { useEffect, useMemo, useRef, useState } from 'react';
import AsyncCreatableSelect from 'react-select/async-creatable';
import { Modal, Spinner } from 'reactstrap';
import { v4 as uuidv4 } from 'uuid';

import {
  analyzeCigarWrapper,
  detectCigars,
  type DetectedCigar,
  enrichCigarHypothesis,
  findSimilarCigarsDebug,
  fingerprintCigarImage,
  hydrateCigarCandidates,
  hydrateSimilarityCandidates,
  identifyCigarDebug,
  type IdentifyResearchMode,
  type IdentifyResearchProfile,
  preprocessCigarImage,
  recognizeCigarFingerprint,
  recognizeCigarText,
} from '@/api/cigar-matching';
import Button from '@/components/overrides/button';
import { getBrands, postCigar } from '@/utils/actions/cigar';
import { uploadToS3 } from '@/utils/imageUtils';

import {
  AUTO_MATCH_CONFIG,
  AUTO_STEP_ORDER,
  type AutoDecision,
  automaticCandidateScore,
  automaticResultSummary,
  type AutoStepKey,
  defaultIdentifyCandidateSelection,
  hasMeaningfulCandidateConflict,
  shouldAcceptCandidate,
  shouldJumpToIdentify,
  shouldRunDetect,
  shouldRunPreprocess,
  shouldRunSimilar,
  shouldRunText,
} from './orchestration';

export type MatchFlowSourceType =
  | 'match_request'
  | 'recognition_test'
  | 'upload';

export interface MatchFlowSource {
  image: string;
  originalImage?: string;
  sourceType: MatchFlowSourceType;
  sourceId?: string | number;
  existingMatch?: unknown;
}

export interface MatchFlowAcceptance {
  candidate: any;
  cigarId: string | number;
  step: string;
  confidence: string | null;
}

interface MatchFlowModalProps {
  isOpen: boolean;
  source: MatchFlowSource | null;
  onClose: () => void;
  onAccept?: (acceptance: MatchFlowAcceptance) => void | Promise<void>;
  acceptButtonLabel?: string;
}

type StepKey =
  | 'image'
  | 'detect'
  | 'preprocess'
  | 'similar'
  | 'text'
  | 'fingerprint'
  | 'fingerprintRecognition'
  | 'identify';
type StepStatus = 'idle' | 'processing' | 'complete' | 'skipped' | 'failed';
type StageState = {
  status: StepStatus;
  result?: any;
  error?: string;
  skipReason?: string;
};

type IdentifyStageKey =
  | 'similar'
  | 'text'
  | 'fingerprint'
  | 'recognize'
  | 'analyze';
type HypothesisImportState = {
  status: 'importing' | 'complete' | 'failed';
  cigar?: any;
  error?: string;
};
type BrandOption = {
  value: string | number;
  label: string;
  __isNew__?: boolean;
};
type EnrichmentPreview = {
  index: number;
  hypothesis: any;
  sourceUrl: string;
  data: Record<string, any>;
  brand: BrandOption | null;
};
type WrapperAnalysisSourceStep =
  | 'similar'
  | 'text'
  | 'fingerprintRecognize'
  | 'identify';
type WrapperAnalysisState = {
  key: string;
  imageUrl: string;
  sourceStep: WrapperAnalysisSourceStep;
  candidateIds: string[];
  result?: any;
  status: 'processing' | 'complete' | 'failed';
  error?: string;
  updatedAt: number;
};
type WorkingImageType = 'original' | 'manual' | 'detected' | 'preprocessed';
type AutoRunStatus =
  | 'idle'
  | 'running'
  | 'stopping'
  | 'waiting_for_selection'
  | 'completed'
  | 'failed';
type AutoRunSummary = {
  candidate?: any;
  label: string | null;
  confidence: number | null;
  confident: boolean;
  finalStep: AutoStepKey;
  reason: string;
  executed: number;
  skipped: number;
  durationMs: number;
};
type AutoRunState = {
  status: AutoRunStatus;
  decisionLog: AutoDecision[];
  startedFromStep?: AutoStepKey;
  stoppedAtStep?: AutoStepKey;
  activeStep?: AutoStepKey;
  startedAt?: number;
  completedAt?: number;
  summary?: AutoRunSummary;
  error?: string;
};

const EMPTY_IDENTIFY_SELECTION = (): Record<IdentifyStageKey, boolean> => ({
  similar: true,
  text: true,
  fingerprint: true,
  recognize: true,
  analyze: true,
});

const rawStageResult = (result: any) => {
  if (!result) return undefined;
  const rawResult = { ...result };
  delete rawResult.hydratedCandidates;
  delete rawResult.hydratedResearchCandidates;
  return rawResult;
};

const formatEvidenceValue = (value: unknown) => {
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  return String(value);
};

const textCandidates = (result: any): any[] =>
  result?.textCandidates || result?.text_candidates || [];

const candidateCigarId = (candidate: any) =>
  candidate?.cigarId ?? candidate?.cigar_id ?? candidate?.id;

const wrapperCandidateId = (candidate: any) =>
  String(candidateCigarId(candidate) ?? candidate?.recognitionId ?? '');

const numericCandidateScore = (candidate: any) => {
  const score =
    candidate?.confidence ?? candidate?.score ?? candidate?.similarity;
  return Number.isFinite(Number(score)) ? Number(score) : null;
};

const getTiedCandidates = (candidateList: any[]) => {
  const scoreGroups = new Map<number, any[]>();
  candidateList.forEach((candidate) => {
    const score = numericCandidateScore(candidate);
    if (score === null || !wrapperCandidateId(candidate)) return;
    scoreGroups.set(score, [...(scoreGroups.get(score) || []), candidate]);
  });
  return [...scoreGroups.values()].find((group) => group.length >= 2) || [];
};

const wrapperAnalysisKey = (candidateList: any[], imageUrl: string) =>
  `${imageUrl}::${candidateList.map(wrapperCandidateId).sort().join('|')}`;

const STEPS: Array<{
  key: StepKey;
  label: string;
  cost: string;
}> = [
  { key: 'image', label: 'Image', cost: 'Source' },
  { key: 'detect', label: 'Detect', cost: 'Free · Local vision' },
  { key: 'preprocess', label: 'Isolate', cost: 'Free' },
  { key: 'similar', label: 'Similar', cost: 'Free · Embeddings' },
  { key: 'text', label: 'Text', cost: 'Paid · Vision' },
  { key: 'fingerprint', label: 'Fingerprint', cost: 'Paid · Vision' },
  {
    key: 'fingerprintRecognition',
    label: 'Recognize',
    cost: 'Free · Database',
  },
  { key: 'identify', label: 'Identify', cost: 'Paid · AI' },
];

const EMPTY_STAGES = (): Record<StepKey, StageState> => ({
  image: { status: 'complete' },
  detect: { status: 'idle' },
  preprocess: { status: 'idle' },
  similar: { status: 'idle' },
  text: { status: 'idle' },
  fingerprint: { status: 'idle' },
  fingerprintRecognition: { status: 'idle' },
  identify: { status: 'idle' },
});

const errorMessage = (error: any) =>
  error?.response?.data?.message ||
  error?.response?.data?.error ||
  error?.message ||
  'This recognition stage failed.';

const percentage = (value: unknown) => {
  const number = Number(value);
  if (!Number.isFinite(number)) return null;
  return `${(number <= 1 ? number * 100 : number).toFixed(1)}%`;
};

const duration = (result: any) => {
  const milliseconds =
    result?.meta?.durationMs ??
    result?.metadata?.processing_ms ??
    result?.processing_ms;
  if (!Number.isFinite(Number(milliseconds))) return null;
  return Number(milliseconds) >= 1000
    ? `${(Number(milliseconds) / 1000).toFixed(1)} sec`
    : `${Number(milliseconds)} ms`;
};

const tokenUsage = (result: any) =>
  result?.meta?.tokenUsage ||
  result?.meta?.token_usage ||
  result?.metadata?.tokenUsage ||
  result?.metadata?.token_usage;

const estimatedCost = (result: any) => {
  const value =
    result?.meta?.estimatedCostUsd ??
    result?.meta?.estimated_cost_usd ??
    result?.metadata?.estimatedCostUsd ??
    result?.metadata?.estimated_cost_usd;
  return Number.isFinite(Number(value)) ? Number(value) : 0;
};

const formatCost = (value: number) =>
  value > 0 && value < 0.0001 ? `$${value.toFixed(6)}` : `$${value.toFixed(4)}`;

const formatDecisionReason = (reason?: string) =>
  reason
    ? reason
        .split('_')
        .map((word) => `${word.charAt(0).toUpperCase()}${word.slice(1)}`)
        .join(' ')
    : '';

const automaticRunReason = (step: AutoStepKey, identifyShortcut: boolean) => {
  if (step === 'detect') return 'source_context_warrants_detection';
  if (step === 'preprocess') return 'image_context_warrants_isolation';
  if (step === 'similar') return 'baseline_embedding_search';
  if (step === 'text') return 'text_presence_unknown_conservative_fallback';
  if (step === 'fingerprint') return 'additional_visual_evidence_required';
  if (step === 'fingerprintRecognition') {
    return 'fingerprint_available_for_catalog_lookup';
  }
  if (identifyShortcut) {
    return 'sufficient_prior_evidence_for_research_confirmation';
  }
  return 'final_research_fallback';
};

const mobileEvidenceMediaQuery = '(max-width: 767px)';

function EvidenceSection({
  children,
  collapsed,
  label,
}: {
  children: React.ReactNode;
  collapsed: boolean;
  label: string;
}) {
  const [open, setOpen] = useState(!collapsed);

  useEffect(() => setOpen(!collapsed), [collapsed]);

  return (
    <details
      className="match-flow-evidence-collapse"
      open={open}
      onToggle={(event) => setOpen(event.currentTarget.open)}
    >
      <summary>{label}</summary>
      <div className="match-flow-evidence-content">{children}</div>
    </details>
  );
}

const uploadPreprocessedImage = async (image: string) => {
  if (!image.startsWith('data:')) return image;
  const response = await fetch(image);
  const blob = await response.blob();
  const id = uuidv4();
  const upload = await uploadToS3(
    blob,
    'cigars/match-debugger',
    `${id}-preprocessed.jpg`,
    id
  );
  return upload.data.media_url;
};

const values = (value: unknown): string[] => {
  if (!value) return [];
  if (Array.isArray(value)) {
    return value
      .map((item: any) => item?.label || item?.name || String(item))
      .filter(Boolean);
  }
  return [String(value)];
};

const candidateLabel = (candidate: any) =>
  candidate?.cigarName ||
  candidate?.label ||
  candidate?.full_name ||
  candidate?.name ||
  candidate?.cigar?.full_name ||
  (candidate?.cigar_id || candidate?.cigarId
    ? `Cigar ${candidate.cigar_id || candidate.cigarId}`
    : 'Candidate');

const candidateScore = (candidate: any) =>
  percentage(
    candidate?.confidence ?? candidate?.score ?? candidate?.similarity
  );

const candidateImage = (candidate: any) =>
  candidate?.bandImageUrl ||
  candidate?.band_image_url ||
  candidate?.displayImageUrl ||
  candidate?.display_image_url ||
  candidate?.cigarImageUrl ||
  candidate?.imageUrl ||
  candidate?.image_url ||
  candidate?.imageId;

const candidateBandImage = (candidate: any) =>
  candidate?.cigarBandImageUrl ||
  candidate?.bandImageUrl ||
  candidate?.band_image_url;

const identificationCandidates = (result: any) => {
  const ranked = [
    ...(result?.match ? [{ ...result.match, resultType: 'match' }] : []),
    ...(result?.suggestions || []).map((candidate: any) => ({
      ...candidate,
      resultType: 'suggestion',
    })),
    ...(result?.consensus?.candidates || []).map((candidate: any) => ({
      ...candidate,
      resultType: 'consensus',
    })),
  ];
  const merged: any[] = [];
  const candidateIndexes = new Map<string, number>();

  ranked.forEach((candidate, index) => {
    const id = candidate.cigar_id ?? candidate.cigarId;
    const key =
      id === undefined || id === null ? `candidate-${index}` : `${id}`;
    const existingIndex = candidateIndexes.get(key);

    if (existingIndex === undefined) {
      candidateIndexes.set(key, merged.length);
      merged.push(candidate);
      return;
    }

    const existing = merged[existingIndex];
    const evidence = [
      ...(Array.isArray(existing.evidence) ? existing.evidence : []),
      ...(Array.isArray(candidate.evidence) ? candidate.evidence : []),
    ].filter((item, evidenceIndex, all) => all.indexOf(item) === evidenceIndex);
    merged[existingIndex] = {
      ...candidate,
      ...existing,
      stageScores: {
        ...(candidate.stageScores || {}),
        ...(existing.stageScores || {}),
      },
      ...(evidence.length ? { evidence } : {}),
    };
  });

  return merged;
};

function MatchFlowModal({
  isOpen,
  source,
  onClose,
  onAccept,
  acceptButtonLabel = 'Accept Match',
}: MatchFlowModalProps) {
  const [activeStep, setActiveStep] = useState<StepKey>('image');
  const [stages, setStages] = useState(EMPTY_STAGES);
  const [workingImage, setWorkingImage] = useState('');
  const [workingImageType, setWorkingImageType] =
    useState<WorkingImageType>('original');
  const [selectedDetectionId, setSelectedDetectionId] = useState<string | null>(
    null
  );
  const [selectedCandidates, setSelectedCandidates] = useState<
    Partial<Record<StepKey, number>>
  >({});
  const [selectedResearchCandidate, setSelectedResearchCandidate] = useState<
    number | null
  >(null);
  const [accepted, setAccepted] = useState<MatchFlowAcceptance | null>(null);
  const [isAccepting, setIsAccepting] = useState(false);
  const [acceptError, setAcceptError] = useState('');
  const [identifySelection, setIdentifySelection] = useState(
    EMPTY_IDENTIFY_SELECTION
  );
  const [researchMode, setResearchMode] =
    useState<IdentifyResearchMode>('auto');
  const [researchProfile, setResearchProfile] =
    useState<IdentifyResearchProfile>('quick');
  const [hypothesisImports, setHypothesisImports] = useState<
    Record<number, HypothesisImportState>
  >({});
  const [enrichmentPreview, setEnrichmentPreview] =
    useState<EnrichmentPreview | null>(null);
  const [savingEnrichment, setSavingEnrichment] = useState(false);
  const [reimportingEnrichment, setReimportingEnrichment] = useState(false);
  const [enrichmentError, setEnrichmentError] = useState('');
  const [wrapperAnalyses, setWrapperAnalyses] = useState<
    WrapperAnalysisState[]
  >([]);
  const [autoRun, setAutoRun] = useState<AutoRunState>({
    status: 'idle',
    decisionLog: [],
  });
  const [collapseEvidence, setCollapseEvidence] = useState(() =>
    typeof window === 'undefined' || typeof window.matchMedia !== 'function'
      ? false
      : window.matchMedia(mobileEvidenceMediaQuery).matches
  );
  const sessionSource = useRef(source);
  const activeStepButton = useRef<HTMLButtonElement | null>(null);
  const autoStopRequested = useRef(false);
  const autoRunning = useRef(false);

  useEffect(() => {
    if (!isOpen || !source) return;
    sessionSource.current = source;
    setActiveStep('image');
    setStages(EMPTY_STAGES());
    setWorkingImage(source.image);
    setWorkingImageType(
      source.sourceType === 'upload' && source.originalImage !== source.image
        ? 'manual'
        : 'original'
    );
    setSelectedDetectionId(null);
    setSelectedCandidates({});
    setSelectedResearchCandidate(null);
    setIdentifySelection(EMPTY_IDENTIFY_SELECTION());
    setResearchMode('auto');
    setResearchProfile('quick');
    setHypothesisImports({});
    setEnrichmentPreview(null);
    setSavingEnrichment(false);
    setReimportingEnrichment(false);
    setEnrichmentError('');
    setWrapperAnalyses([]);
    setAutoRun({ status: 'idle', decisionLog: [] });
    autoStopRequested.current = false;
    autoRunning.current = false;
    setAccepted(null);
    setIsAccepting(false);
    setAcceptError('');
  }, [isOpen, source]);

  useEffect(() => {
    if (
      !isOpen ||
      typeof window === 'undefined' ||
      typeof window.matchMedia !== 'function' ||
      !window.matchMedia('(max-width: 767px)').matches
    ) {
      return;
    }
    activeStepButton.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'center',
    });
  }, [activeStep, isOpen]);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mediaQuery = window.matchMedia(mobileEvidenceMediaQuery);
    const updateEvidenceLayout = () => setCollapseEvidence(mediaQuery.matches);
    updateEvidenceLayout();
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', updateEvidenceLayout);
      return () =>
        mediaQuery.removeEventListener('change', updateEvidenceLayout);
    }
    mediaQuery.addListener(updateEvidenceLayout);
    return () => mediaQuery.removeListener(updateEvidenceLayout);
  }, []);

  useEffect(() => {
    const { result } = stages.identify;
    if (!result) return;
    const selection = defaultIdentifyCandidateSelection(result);

    if (selection?.source === 'research') {
      setSelectedResearchCandidate(selection.index);
      setSelectedCandidates((current) => ({
        ...current,
        identify: -1,
      }));
      return;
    }

    if (selection?.source === 'hypothesis') {
      setSelectedResearchCandidate(null);
      setSelectedCandidates((current) => ({ ...current, identify: -1 }));
      return;
    }

    setSelectedResearchCandidate(null);
    setSelectedCandidates((current) => {
      const next = { ...current };
      if (selection) next.identify = selection.index;
      else delete next.identify;
      return next;
    });
  }, [stages.identify.result]);

  const sourceImage = source?.originalImage || source?.image || '';
  const stage = stages[activeStep];
  const activeIndex = STEPS.findIndex(({ key }) => key === activeStep);
  const automaticIsActive = ['running', 'stopping'].includes(autoRun.status);
  const similarBlockedByIsolation =
    activeStep === 'similar' && !shouldRunSimilar(stages.preprocess.status);

  const clearDownstream = (step: StepKey) => {
    const start = STEPS.findIndex(({ key }) => key === step) + 1;
    setStages((current) => {
      const next = { ...current };
      STEPS.slice(start).forEach(({ key }) => {
        next[key] = { status: 'idle' };
      });
      return next;
    });
    setAccepted(null);
  };

  const selectWorkingImage = (
    image: string,
    type: WorkingImageType,
    detectionId?: string
  ) => {
    if (!image || image === workingImage || autoRunning.current) return;
    setWorkingImage(image);
    setWorkingImageType(type);
    if (type === 'detected') setSelectedDetectionId(detectionId || null);
    if (type === 'original' || type === 'manual') setSelectedDetectionId(null);
    let sourceStep: StepKey = 'image';
    if (type === 'detected') sourceStep = 'detect';
    if (type === 'preprocessed') sourceStep = 'preprocess';
    clearDownstream(sourceStep);
    if (autoRun.decisionLog.length > 0) {
      setAutoRun((current) => ({
        ...current,
        status: 'idle',
        activeStep: undefined,
        stoppedAtStep: undefined,
        summary: undefined,
        decisionLog: [
          ...current.decisionLog,
          {
            id: uuidv4(),
            step: type === 'preprocessed' ? 'preprocess' : 'detect',
            action: 'manual_override',
            reason:
              type === 'detected'
                ? 'admin_selected_detected_cigar'
                : 'admin_changed_working_image',
            completedAt: Date.now(),
            detail: detectionId,
          },
        ],
      }));
    }
  };

  const executeStage = async (
    key: AutoStepKey,
    currentStages: Record<StepKey, StageState>,
    imageUrl: string
  ) => {
    let result: any;
    if (key === 'detect') result = await detectCigars(imageUrl);
    if (key === 'preprocess') {
      result = await preprocessCigarImage(imageUrl);
      if (result.image) {
        try {
          result.publicImageUrl = await uploadPreprocessedImage(result.image);
        } catch (uploadError) {
          result.uploadError = errorMessage(uploadError);
        }
      }
    }
    if (key === 'similar') {
      result = await findSimilarCigarsDebug(imageUrl);
      result.hydratedCandidates = await hydrateSimilarityCandidates(
        result.candidates || []
      );
    }
    if (key === 'text') {
      result = await recognizeCigarText(imageUrl);
      const ranked = [
        ...(result.proposedMatch
          ? [{ ...result.proposedMatch, proposed: true }]
          : []),
        ...textCandidates(result),
      ].filter(
        (candidate, index, all) =>
          all.findIndex(
            (item) =>
              (item.cigarId || item.cigar_id) ===
              (candidate.cigarId || candidate.cigar_id)
          ) === index
      );
      result.hydratedCandidates = await hydrateCigarCandidates(
        ranked.map((candidate) => ({
          ...candidate,
          cigar_id: candidate.cigar_id || candidate.cigarId,
        })),
        10
      );
    }
    if (key === 'fingerprint') result = await fingerprintCigarImage(imageUrl);
    if (key === 'fingerprintRecognition') {
      const fingerprint = currentStages.fingerprint.result?.fingerprint;
      if (!fingerprint) throw new Error('Generate a fingerprint first.');
      result = await recognizeCigarFingerprint(fingerprint);
      result.hydratedCandidates = await hydrateCigarCandidates(
        (result.candidates || []).map((candidate: any) => ({
          ...candidate,
          cigar_id: candidate.cigar_id || candidate.cigarId,
        })),
        10
      );
    }
    if (key === 'identify') {
      const stageResults: Record<string, unknown> = {};
      if (identifySelection.similar && currentStages.similar.result) {
        stageResults.similar = rawStageResult(currentStages.similar.result);
      }
      if (identifySelection.text && currentStages.text.result) {
        stageResults.text = rawStageResult(currentStages.text.result);
      }
      if (identifySelection.fingerprint && currentStages.fingerprint.result) {
        stageResults.fingerprint = rawStageResult(
          currentStages.fingerprint.result
        );
      }
      if (
        identifySelection.recognize &&
        currentStages.fingerprintRecognition.result
      ) {
        stageResults.recognize = rawStageResult(
          currentStages.fingerprintRecognition.result
        );
      }
      const relevantWrapperSteps = new Set<WrapperAnalysisSourceStep>([
        ...(identifySelection.similar
          ? (['similar'] as WrapperAnalysisSourceStep[])
          : []),
        ...(identifySelection.text
          ? (['text'] as WrapperAnalysisSourceStep[])
          : []),
        ...(identifySelection.recognize
          ? (['fingerprintRecognize'] as WrapperAnalysisSourceStep[])
          : []),
      ]);
      const selectedWrapperAnalysis = [...wrapperAnalyses]
        .filter(
          (analysis) =>
            analysis.status === 'complete' &&
            analysis.imageUrl === imageUrl &&
            relevantWrapperSteps.has(analysis.sourceStep)
        )
        .sort((left, right) => right.updatedAt - left.updatedAt)[0];
      const wrapperAnalysis =
        selectedWrapperAnalysis ||
        [...wrapperAnalyses]
          .filter(
            (analysis) =>
              analysis.status === 'complete' && analysis.imageUrl === imageUrl
          )
          .sort((left, right) => right.updatedAt - left.updatedAt)[0];
      if (identifySelection.analyze && wrapperAnalysis?.result) {
        const wrapperResult = wrapperAnalysis.result;
        stageResults.wrapperAnalysis = {
          sourceStep: wrapperAnalysis.sourceStep,
          ambiguity: wrapperResult.ambiguity,
          predictedShade:
            wrapperResult.predictedShade ||
            wrapperResult.predicted ||
            wrapperResult.result?.predicted,
          confidence:
            wrapperResult.confidence || wrapperResult.result?.confidence,
          evidence: wrapperResult.evidence,
          candidateAdjustments: wrapperResult.candidateAdjustments,
          inconclusive: wrapperResult.inconclusive,
          conclusion: wrapperResult.conclusion,
        };
      }
      result = await identifyCigarDebug(
        imageUrl,
        stageResults,
        researchMode,
        researchProfile
      );
      const identifyCandidates = identificationCandidates(result).map(
        (candidate: any) => ({
          ...candidate,
          cigar_id: candidate.cigar_id ?? candidate.cigarId,
        })
      );
      result.hydratedCandidates = await hydrateCigarCandidates(
        identifyCandidates,
        identifyCandidates.length
      );
      result.hydratedResearchCandidates = await hydrateCigarCandidates(
        (result.research?.catalogCandidates || []).map((candidate: any) => ({
          ...candidate,
          cigar_id: candidate.cigar_id || candidate.cigarId,
        })),
        10
      );
    }
    return result;
  };

  const runStage = async (key: StepKey) => {
    if (
      key === 'image' ||
      !workingImage ||
      stages[key].status === 'processing' ||
      autoRunning.current
    ) {
      return undefined;
    }
    if (key === 'similar' && !shouldRunSimilar(stages.preprocess.status)) {
      setActiveStep('similar');
      setStages((current) => ({
        ...current,
        similar: {
          status: 'skipped',
          skipReason: 'isolation_skipped_similarity_requires_band_crop',
        },
      }));
      return undefined;
    }
    const manualStartedAt = Date.now();
    if (key === 'identify') {
      setSelectedResearchCandidate(null);
      setSelectedCandidates((current) => {
        const next = { ...current };
        delete next.identify;
        return next;
      });
    }
    setActiveStep(key);
    setStages((current) => ({
      ...current,
      [key]: { ...current[key], status: 'processing', error: undefined },
    }));

    try {
      const result = await executeStage(key, stages, workingImage);

      setStages((current) => ({
        ...current,
        [key]: { status: 'complete', result },
      }));
      if (key === 'preprocess' && workingImageType === 'preprocessed') {
        if (result.publicImageUrl) setWorkingImage(result.publicImageUrl);
        else {
          setWorkingImage(source?.image || sourceImage);
          setWorkingImageType(
            source?.image !== sourceImage ? 'manual' : 'original'
          );
        }
      }
      if (autoRun.decisionLog.length > 0) {
        const completedAt = Date.now();
        const strength = shouldAcceptCandidate(key, result);
        setAutoRun((current) => ({
          ...current,
          status: 'idle',
          summary: undefined,
          stoppedAtStep: undefined,
          decisionLog: [
            ...current.decisionLog,
            {
              id: uuidv4(),
              step: key,
              action: 'manual_override',
              reason: 'admin_ran_step_manually',
              startedAt: manualStartedAt,
              completedAt,
              durationMs: completedAt - manualStartedAt,
              confidence: strength.confidence ?? undefined,
            },
          ],
        }));
      }
      return result;
    } catch (error) {
      setStages((current) => ({
        ...current,
        [key]: {
          ...current[key],
          status: 'failed',
          error: errorMessage(error),
        },
      }));
      if (autoRun.decisionLog.length > 0) {
        const completedAt = Date.now();
        setAutoRun((current) => ({
          ...current,
          status: 'idle',
          summary: undefined,
          stoppedAtStep: undefined,
          decisionLog: [
            ...current.decisionLog,
            {
              id: uuidv4(),
              step: key,
              action: 'failed',
              reason: 'manual_override_failed',
              detail: errorMessage(error),
              startedAt: manualStartedAt,
              completedAt,
              durationMs: completedAt - manualStartedAt,
            },
          ],
        }));
      }
      return undefined;
    }
  };

  const recordManualSkip = (step: AutoStepKey) => {
    if (autoRun.decisionLog.length === 0) return;
    setAutoRun((current) => ({
      ...current,
      status: 'idle',
      summary: undefined,
      stoppedAtStep: undefined,
      decisionLog: [
        ...current.decisionLog,
        {
          id: uuidv4(),
          step,
          action: 'manual_override',
          reason: 'admin_skipped_step_manually',
          completedAt: Date.now(),
        },
      ],
    }));
  };

  const skipPreprocess = () => {
    setStages((current) => ({
      ...current,
      preprocess: {
        ...current.preprocess,
        status: 'skipped',
        skipReason: 'isolation_skipped_by_admin',
      },
      similar: {
        status: 'skipped',
        skipReason: 'isolation_skipped_similarity_requires_band_crop',
      },
    }));
    recordManualSkip('preprocess');
    recordManualSkip('similar');
    setAccepted(null);
    setActiveStep('text');
  };

  const skipDetect = () => {
    setStages((current) => ({
      ...current,
      detect: { ...current.detect, status: 'skipped' },
    }));
    recordManualSkip('detect');
    setActiveStep('preprocess');
  };

  const skipDetectAndRunPreprocess = () => {
    skipDetect();
    runStage('preprocess');
  };

  const skipToText = () => {
    setStages((current) => ({
      ...current,
      detect: {
        ...current.detect,
        status: 'skipped',
        skipReason: 'detection_skipped_by_admin',
      },
      preprocess: {
        ...current.preprocess,
        status: 'skipped',
        skipReason: 'isolation_skipped_by_admin',
      },
      similar: {
        status: 'skipped',
        skipReason: 'isolation_skipped_similarity_requires_band_crop',
      },
    }));
    recordManualSkip('detect');
    recordManualSkip('preprocess');
    recordManualSkip('similar');
    setAccepted(null);
    setActiveStep('text');
  };

  /* eslint-disable no-await-in-loop, no-continue -- The orchestration policy intentionally executes one dependent stage at a time. */
  const runAutomatically = async (fromHere = false) => {
    if (!source || !workingImage || autoRunning.current) return;
    autoRunning.current = true;
    autoStopRequested.current = false;
    const startedAt = Date.now();
    const currentAutoStep =
      activeStep === 'image' ? 'detect' : (activeStep as AutoStepKey);
    const startedFromStep = fromHere ? currentAutoStep : 'detect';
    const startIndex = fromHere
      ? Math.max(0, AUTO_STEP_ORDER.indexOf(startedFromStep))
      : 0;
    let localStages = { ...stages };
    let localImage = workingImage;
    let localImageType = workingImageType;
    let decisions = fromHere ? [...autoRun.decisionLog] : [];
    const decisionStartIndex = decisions.length;
    let finalStep: AutoStepKey = startedFromStep;
    let finalResult: any;
    let finalReason = 'automatic_flow_completed';
    let identifyShortcut = false;

    const publish = (next: AutoDecision) => {
      decisions = [...decisions, next];
      setAutoRun((current) => ({
        ...current,
        status: 'running',
        decisionLog: decisions,
        activeStep: next.step,
        startedAt,
        startedFromStep,
        error: undefined,
        summary: undefined,
      }));
    };
    const skip = (
      step: AutoStepKey,
      reason: string,
      detail?: string,
      confidence?: number | null,
      forceSkipped = false
    ) => {
      const now = Date.now();
      localStages = {
        ...localStages,
        [step]: {
          ...localStages[step],
          status:
            !forceSkipped && localStages[step].status === 'complete'
              ? 'complete'
              : 'skipped',
          skipReason: reason,
        },
      };
      setStages(localStages);
      publish({
        id: uuidv4(),
        step,
        action: 'skipped',
        reason,
        detail,
        confidence: confidence ?? undefined,
        completedAt: now,
      });
    };
    const invalidateAfterImageChange = (step: AutoStepKey) => {
      const nextStages = { ...localStages };
      const changedAt = AUTO_STEP_ORDER.indexOf(step);
      AUTO_STEP_ORDER.slice(changedAt + 1).forEach((downstreamStep) => {
        nextStages[downstreamStep] = { status: 'idle' };
      });
      localStages = nextStages;
      setStages(localStages);
    };
    const finish = (
      status: AutoRunStatus,
      stoppedAtStep: AutoStepKey,
      reason: string,
      result?: any
    ) => {
      const completedAt = Date.now();
      const summary = automaticResultSummary(
        stoppedAtStep,
        result,
        AUTO_MATCH_CONFIG
      );
      const runDecisions = decisions.slice(decisionStartIndex);
      setAutoRun({
        status,
        decisionLog: decisions,
        startedFromStep,
        stoppedAtStep,
        startedAt,
        completedAt,
        summary: {
          ...summary,
          finalStep: stoppedAtStep,
          reason,
          executed: runDecisions.filter((decision) => decision.action === 'run')
            .length,
          skipped: runDecisions.filter(
            (decision) => decision.action === 'skipped'
          ).length,
          durationMs: completedAt - startedAt,
        },
      });
      autoRunning.current = false;
    };

    setAutoRun({
      status: 'running',
      decisionLog: decisions,
      startedFromStep,
      activeStep: startedFromStep,
      startedAt,
    });

    try {
      for (let index = startIndex; index < AUTO_STEP_ORDER.length; index += 1) {
        const key = AUTO_STEP_ORDER[index];
        finalStep = key;
        if (autoStopRequested.current) {
          finalReason = 'stopped_by_admin';
          finish('completed', finalStep, finalReason, finalResult);
          return;
        }

        if (
          identifyShortcut &&
          key !== 'identify' &&
          ['fingerprint', 'fingerprintRecognition'].includes(key)
        ) {
          skip(key, 'sufficient_prior_evidence_for_research_confirmation');
          continue;
        }

        if (
          key === 'similar' &&
          !shouldRunSimilar(localStages.preprocess.status)
        ) {
          skip(
            key,
            'isolation_skipped_similarity_requires_band_crop',
            undefined,
            undefined,
            true
          );
          continue;
        }

        const existing = localStages[key];
        if (existing.status === 'complete' && existing.result) {
          const existingStrength = shouldAcceptCandidate(key, existing.result);
          const existingDetections: DetectedCigar[] =
            key === 'detect' ? existing.result.cigars || [] : [];
          const reuseDetail =
            existingDetections.length === 1
              ? `Continuing with ${existingDetections[0].detectionId}`
              : undefined;
          skip(
            key,
            'valid_result_reused',
            reuseDetail,
            existingStrength.confidence
          );
          finalResult = existing.result;
          if (key === 'detect') {
            const detected = existingDetections;
            if (detected.length === 1 && !selectedDetectionId) {
              const [detection] = detected;
              localImage = detection.image;
              localImageType = 'detected';
              setWorkingImage(detection.image);
              setWorkingImageType('detected');
              setSelectedDetectionId(detection.detectionId);
              invalidateAfterImageChange('detect');
            }
            if (detected.length > 1 && !selectedDetectionId) {
              setActiveStep('detect');
              setAutoRun((current) => ({
                ...current,
                status: 'waiting_for_selection',
                activeStep: 'detect',
                stoppedAtStep: 'detect',
              }));
              autoRunning.current = false;
              return;
            }
          }
          if (key === 'preprocess' && localImageType !== 'preprocessed') {
            const processedImage =
              existing.result.publicImageUrl || existing.result.image;
            if (processedImage) {
              localImage = processedImage;
              localImageType = 'preprocessed';
              setWorkingImage(processedImage);
              setWorkingImageType('preprocessed');
              invalidateAfterImageChange('preprocess');
            }
          }
          if (
            ['similar', 'text', 'fingerprintRecognition', 'identify'].includes(
              key
            )
          ) {
            const strength = existingStrength;
            if (
              strength.sufficient &&
              !hasMeaningfulCandidateConflict(localStages, key)
            ) {
              publish({
                id: uuidv4(),
                step: key,
                action: 'stop',
                reason: 'high_confidence_match',
                confidence: strength.confidence || undefined,
                completedAt: Date.now(),
              });
              setActiveStep(key);
              finish(
                'completed',
                key,
                'high_confidence_match',
                existing.result
              );
              return;
            }
          }
          if (key === 'text' && shouldJumpToIdentify(localStages)) {
            identifyShortcut = true;
          }
          continue;
        }

        if (
          key === 'detect' &&
          !shouldRunDetect(source.sourceType, localImageType)
        ) {
          skip(key, 'single_cigar_input_context');
          continue;
        }
        if (
          key === 'preprocess' &&
          !shouldRunPreprocess(source.sourceType, localImageType)
        ) {
          skip(key, 'preprocessing_not_expected_to_improve_input');
          continue;
        }
        if (key === 'text') {
          const textDecision = shouldRunText({
            detectResult: localStages.detect.result,
            preprocessResult: localStages.preprocess.result,
          });
          if (!textDecision.run) {
            skip(key, textDecision.reason);
            continue;
          }
        }
        if (
          key === 'fingerprintRecognition' &&
          !localStages.fingerprint.result?.fingerprint
        ) {
          skip(key, 'fingerprint_unavailable');
          continue;
        }

        const stageStartedAt = Date.now();
        setActiveStep(key);
        localStages = {
          ...localStages,
          [key]: { status: 'processing' },
        };
        setStages(localStages);
        setAutoRun((current) => ({
          ...current,
          status: 'running',
          activeStep: key,
        }));

        try {
          const result = await executeStage(key, localStages, localImage);
          const completedAt = Date.now();
          const completedStrength = shouldAcceptCandidate(key, result);
          finalResult = result;
          localStages = {
            ...localStages,
            [key]: { status: 'complete', result },
          };
          setStages(localStages);
          publish({
            id: uuidv4(),
            step: key,
            action: 'run',
            reason: automaticRunReason(key, identifyShortcut),
            startedAt: stageStartedAt,
            completedAt,
            durationMs: completedAt - stageStartedAt,
            confidence: completedStrength.confidence ?? undefined,
            detail:
              key === 'detect'
                ? `${result?.detectedCount || 0} cigars found${result?.cigars?.length === 1 ? ` · continuing with ${result.cigars[0].detectionId}` : ''}`
                : undefined,
          });

          if (autoStopRequested.current) {
            finish('completed', key, 'stopped_by_admin', result);
            return;
          }

          if (key === 'detect') {
            const detections: DetectedCigar[] = result?.cigars || [];
            if (detections.length === 1) {
              const [detection] = detections;
              localImage = detection.image;
              localImageType = 'detected';
              setWorkingImage(detection.image);
              setWorkingImageType('detected');
              setSelectedDetectionId(detection.detectionId);
              invalidateAfterImageChange('detect');
            } else if (detections.length > 1) {
              setActiveStep('detect');
              setAutoRun((current) => ({
                ...current,
                status: 'waiting_for_selection',
                activeStep: 'detect',
                stoppedAtStep: 'detect',
              }));
              autoRunning.current = false;
              return;
            }
          }
          if (key === 'preprocess') {
            const processedImage = result?.publicImageUrl || result?.image;
            if (processedImage) {
              localImage = processedImage;
              localImageType = 'preprocessed';
              setWorkingImage(processedImage);
              setWorkingImageType('preprocessed');
              invalidateAfterImageChange('preprocess');
            }
          }

          if (
            ['similar', 'text', 'fingerprintRecognition', 'identify'].includes(
              key
            )
          ) {
            const strength = completedStrength;
            if (
              strength.sufficient &&
              !hasMeaningfulCandidateConflict(localStages, key)
            ) {
              publish({
                id: uuidv4(),
                step: key,
                action: 'stop',
                reason: 'high_confidence_match',
                confidence: strength.confidence || undefined,
                completedAt: Date.now(),
              });
              finish('completed', key, 'high_confidence_match', result);
              return;
            }
          }
          if (key === 'text' && shouldJumpToIdentify(localStages)) {
            identifyShortcut = true;
          }
        } catch (error) {
          const completedAt = Date.now();
          localStages = {
            ...localStages,
            [key]: {
              status: 'failed',
              error: errorMessage(error),
            },
          };
          setStages(localStages);
          publish({
            id: uuidv4(),
            step: key,
            action: 'failed',
            reason: 'stage_request_failed',
            detail: errorMessage(error),
            startedAt: stageStartedAt,
            completedAt,
            durationMs: completedAt - stageStartedAt,
          });
        }
      }
      finish('completed', finalStep, finalReason, finalResult);
    } catch (error) {
      autoRunning.current = false;
      setAutoRun((current) => ({
        ...current,
        status: 'failed',
        error: errorMessage(error),
        completedAt: Date.now(),
      }));
    }
  };
  /* eslint-enable no-await-in-loop, no-continue */

  const stopAutomaticRun = () => {
    if (!autoRunning.current) return;
    autoStopRequested.current = true;
    setAutoRun((current) => ({ ...current, status: 'stopping' }));
  };

  const candidates = useMemo(() => {
    const { result } = stage;
    if (!result) return [];
    if (result.hydratedCandidates?.length) return result.hydratedCandidates;
    if (activeStep === 'text') {
      const recognizedCandidates = textCandidates(result);
      if (recognizedCandidates.length) return recognizedCandidates;
      return (
        result.cigarCandidates ||
        result.cigar_candidates ||
        result.candidates ||
        []
      );
    }
    return result.candidates || [];
  }, [activeStep, stage.result]);

  const researchCandidates = useMemo(() => {
    if (activeStep !== 'identify') return [];
    return stage.result?.hydratedResearchCandidates?.length
      ? stage.result.hydratedResearchCandidates
      : stage.result?.research?.catalogCandidates || [];
  }, [activeStep, stage.result]);

  const runUsage = useMemo(
    () =>
      STEPS.reduce(
        (total, { key }) => {
          const { result } = stages[key];
          const usage = tokenUsage(result);
          return {
            tokens:
              total.tokens +
              Number(usage?.totalTokens ?? usage?.total_tokens ?? 0),
            cost: total.cost + estimatedCost(result),
          };
        },
        { tokens: 0, cost: 0 }
      ),
    [stages]
  );

  const selectedCandidate =
    activeStep === 'identify' && selectedResearchCandidate !== null
      ? researchCandidates[selectedResearchCandidate]
      : candidates[selectedCandidates[activeStep] ?? 0];

  const renderIdentifyResultSummary = () => {
    if (
      activeStep !== 'identify' ||
      stage.status !== 'complete' ||
      autoRun.summary?.finalStep === 'identify'
    ) {
      return null;
    }

    const summary = automaticResultSummary('identify', stage.result);
    let finalCandidate = selectedCandidate || summary.candidate;
    if (stage.result?.status !== 'matched') {
      finalCandidate =
        selectedResearchCandidate !== null
          ? selectedCandidate
          : summary.candidate;
    }
    const confidence =
      automaticCandidateScore(finalCandidate) ?? summary.confidence;
    let selectedSource = 'Match and suggestions';
    if (stage.result?.status !== 'matched') {
      selectedSource =
        selectedResearchCandidate !== null
          ? 'Research catalog candidates'
          : 'Research hypothesis';
    }
    const decisionReason =
      stage.result?.metadata?.decision_reason ||
      stage.result?.metadata?.decisionReason ||
      stage.result?.research?.decisionReason ||
      stage.result?.consensus?.decisionReason;

    return (
      <section className="match-flow-identify-result">
        <div className="match-flow-auto-summary">
          <strong>
            {stage.result?.status === 'matched'
              ? 'Identification Complete'
              : 'Suggested Match'}
          </strong>
          {finalCandidate ? (
            <h5>{candidateLabel(finalCandidate)}</h5>
          ) : (
            <h5>No confident match found.</h5>
          )}
          {confidence !== null && (
            <span>Confidence: {percentage(confidence)}</span>
          )}
          {finalCandidate && <span>Selected from: {selectedSource}</span>}
          {stage.result?.status && (
            <span>Result: {formatDecisionReason(stage.result.status)}</span>
          )}
          {decisionReason && (
            <span>Reason: {formatDecisionReason(decisionReason)}</span>
          )}
          {!summary.confident && (
            <span>Recommendation: Manual review required.</span>
          )}
        </div>
      </section>
    );
  };
  const acceptCandidate = async (
    candidate = selectedCandidate,
    step: StepKey = activeStep,
    confidence = candidateScore(candidate)
  ) => {
    if (!candidate || isAccepting) return;
    const cigarId = candidateCigarId(candidate);
    if (cigarId === undefined || cigarId === null || cigarId === '') {
      setAcceptError('The selected candidate does not have a cigar ID.');
      return;
    }
    const acceptance: MatchFlowAcceptance = {
      candidate,
      cigarId,
      step,
      confidence,
    };
    setIsAccepting(true);
    setAcceptError('');
    try {
      await onAccept?.(acceptance);
      setAccepted(acceptance);
    } catch (error) {
      setAcceptError(errorMessage(error));
    } finally {
      setIsAccepting(false);
    }
  };

  const nextStep = () => {
    const next = STEPS[activeIndex + 1];
    if (!next) return;
    setActiveStep(next.key);
    if (stages[next.key].status === 'idle') runStage(next.key);
  };

  const renderEvidence = (
    content: React.ReactNode,
    label = 'View step evidence'
  ) => (
    <EvidenceSection collapsed={collapseEvidence} label={label}>
      {content}
    </EvidenceSection>
  );

  const renderCandidateEvidence = () => {
    const evidencedCandidates = candidates.filter(
      (candidate: any) => values(candidate.evidence).length > 0
    );
    if (!evidencedCandidates.length) return null;
    return (
      <div className="match-flow-mobile-candidate-evidence">
        <strong>Candidate evidence</strong>
        {evidencedCandidates.map((candidate: any, index: number) => (
          <div key={`${candidateCigarId(candidate) ?? index}-evidence`}>
            <b>{candidateLabel(candidate)}</b>
            <span>{values(candidate.evidence).join(' · ')}</span>
          </div>
        ))}
      </div>
    );
  };

  const renderMeta = (result: any, fallbackCost: string) => (
    <div className="match-flow-meta">
      <span>
        {result?.meta?.paidInference ? 'Paid inference' : fallbackCost}
      </span>
      {duration(result) && <span>{duration(result)}</span>}
      {(result?.meta?.provider || result?.metadata?.source) && (
        <span>{result.meta?.provider || result.metadata?.source}</span>
      )}
      {(result?.meta?.model || result?.metadata?.multimodal_model) && (
        <span>{result.meta?.model || result.metadata?.multimodal_model}</span>
      )}
      {tokenUsage(result) && (
        <span>
          {Number(
            tokenUsage(result).totalTokens ??
              tokenUsage(result).total_tokens ??
              0
          ).toLocaleString()}{' '}
          tokens ·{' '}
          {Number(
            tokenUsage(result).inputTokens ??
              tokenUsage(result).input_tokens ??
              0
          ).toLocaleString()}{' '}
          in /{' '}
          {Number(
            tokenUsage(result).outputTokens ??
              tokenUsage(result).output_tokens ??
              0
          ).toLocaleString()}{' '}
          out
        </span>
      )}
      {estimatedCost(result) > 0 && (
        <span>{formatCost(estimatedCost(result))} estimated</span>
      )}
      {(result?.meta?.costBasis || result?.meta?.cost_basis) && (
        <span>
          {(result.meta.costBasis || result.meta.cost_basis)
            .split('_')
            .join(' ')}
        </span>
      )}
    </div>
  );

  const currentWrapperSourceStep = (): WrapperAnalysisSourceStep | null => {
    if (activeStep === 'similar' || activeStep === 'text') return activeStep;
    if (activeStep === 'fingerprintRecognition') {
      return 'fingerprintRecognize';
    }
    if (activeStep === 'identify') return 'identify';
    return null;
  };

  const currentRawCandidates = () => {
    const { result } = stage;
    if (!result) return [];
    if (activeStep === 'text') {
      return [
        ...(result.proposedMatch ? [result.proposedMatch] : []),
        ...textCandidates(result),
      ];
    }
    if (activeStep === 'identify') {
      return identificationCandidates(result);
    }
    return result.candidates || [];
  };

  const runWrapperAnalysis = async (
    allCandidates: any[],
    tiedCandidates: any[],
    sourceStep: WrapperAnalysisSourceStep
  ) => {
    const key = wrapperAnalysisKey(tiedCandidates, workingImage);
    const current = wrapperAnalyses.find((analysis) => analysis.key === key);
    if (current?.status === 'processing') return;
    const candidateIds = tiedCandidates.map(wrapperCandidateId).sort();
    const pending: WrapperAnalysisState = {
      key,
      imageUrl: workingImage,
      sourceStep,
      candidateIds,
      status: 'processing',
      updatedAt: Date.now(),
    };
    setWrapperAnalyses((analyses) => [
      ...analyses.filter((analysis) => analysis.key !== key),
      pending,
    ]);
    try {
      const result = await analyzeCigarWrapper({
        imageUrl: workingImage,
        candidates: allCandidates,
      });
      setWrapperAnalyses((analyses) => [
        ...analyses.filter((analysis) => analysis.key !== key),
        {
          ...pending,
          sourceStep,
          result,
          status: 'complete',
          updatedAt: Date.now(),
        },
      ]);
    } catch (error) {
      setWrapperAnalyses((analyses) => [
        ...analyses.filter((analysis) => analysis.key !== key),
        {
          ...pending,
          status: 'failed',
          error: errorMessage(error),
          updatedAt: Date.now(),
        },
      ]);
    }
  };

  const renderWrapperAnalysis = () => {
    const sourceStep = currentWrapperSourceStep();
    const allCandidates = currentRawCandidates();
    const tiedCandidates = getTiedCandidates(allCandidates);
    if (!sourceStep || tiedCandidates.length < 2) return null;
    const key = wrapperAnalysisKey(tiedCandidates, workingImage);
    const analysis = wrapperAnalyses.find((item) => item.key === key);
    const result = analysis?.result;
    const predicted =
      result?.predictedShade ||
      result?.predicted ||
      result?.favored ||
      result?.result?.predicted;
    const wrapperConfidence = result?.confidence || result?.result?.confidence;
    const ambiguity =
      result?.ambiguity?.values ||
      result?.ambiguity?.label ||
      result?.ambiguity;
    const evidenceEntries = Object.entries(result?.evidence || {});
    const adjustments =
      result?.candidateAdjustments || result?.candidate_adjustments || [];
    const inconclusive = Boolean(
      result?.skipped ||
        result?.analyzed === false ||
        result?.inconclusive ||
        result?.conclusive === false ||
        result?.status === 'inconclusive'
    );
    let analysisButtonLabel = 'Analyze Wrapper';
    if (analysis?.status === 'processing') {
      analysisButtonLabel = 'Analyzing Wrapper…';
    } else if (analysis?.status === 'complete') {
      analysisButtonLabel = 'Run Again';
    }
    let resultLabel = 'No favored wrapper returned';
    if (inconclusive) {
      resultLabel = 'Inconclusive';
    } else if (predicted) {
      const confidenceLabel = percentage(wrapperConfidence);
      resultLabel = `${predicted} favored${confidenceLabel ? ` — ${confidenceLabel}` : ''}`;
    }

    return (
      <section className="match-flow-wrapper-analysis">
        <div className="match-flow-wrapper-analysis-heading">
          <div>
            <strong>Wrapper Analysis</strong>
            {analysis?.status === 'complete' && (
              <span>
                Wrapper analyzed
                {predicted ? ` — ${predicted} favored` : ''}
                {analysis.sourceStep !== sourceStep ? ' · reused' : ''}
              </span>
            )}
          </div>
          <Button
            size="sm"
            color="secondary"
            disabled={analysis?.status === 'processing'}
            onClick={() =>
              runWrapperAnalysis(allCandidates, tiedCandidates, sourceStep)
            }
          >
            {analysisButtonLabel}
          </Button>
        </div>
        {analysis?.status === 'failed' && (
          <div className="match-flow-wrapper-error">{analysis.error}</div>
        )}
        {analysis?.status === 'complete' && (
          <>
            {renderEvidence(
              <div className="match-flow-wrapper-result">
                {ambiguity && (
                  <div>
                    <small>Ambiguity</small>
                    <strong>{values(ambiguity).join(' vs ')}</strong>
                  </div>
                )}
                <div>
                  <small>Result</small>
                  <strong>{resultLabel}</strong>
                  {(result.explanation ||
                    result.reasoning ||
                    result.message) && (
                    <p>
                      {result.explanation || result.reasoning || result.message}
                    </p>
                  )}
                </div>
                {evidenceEntries.length > 0 && (
                  <div>
                    <small>Evidence</small>
                    <ul>
                      {evidenceEntries.map(([label, value]) => (
                        <li key={label}>
                          {label
                            .replace(/([a-z])([A-Z])/g, '$1 $2')
                            .split('_')
                            .join(' ')}
                          : {formatEvidenceValue(value)}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {adjustments.length > 0 && (
                  <div>
                    <small>Candidate adjustments</small>
                    <ul>
                      {adjustments.map((adjustment: any, index: number) => (
                        <li key={adjustment.candidateId || index}>
                          {adjustment.label ||
                            adjustment.name ||
                            (adjustment.wrapperShade
                              ? `${adjustment.wrapperShade} · cigar ${adjustment.cigarId}`
                              : null) ||
                            adjustment.candidateId ||
                            'Candidate'}{' '}
                          {Number(
                            adjustment.scoreAdjustment ?? adjustment.adjustment
                          ) >= 0
                            ? '+'
                            : ''}
                          {adjustment.scoreAdjustment ?? adjustment.adjustment}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                <details>
                  <summary>Raw wrapper response</summary>
                  <pre>{JSON.stringify(result, null, 2)}</pre>
                </details>
              </div>,
              'View wrapper evidence'
            )}
          </>
        )}
      </section>
    );
  };

  const renderCandidates = () => (
    <>
      <div className="match-flow-candidates">
        {!candidates.length && <p>No candidate identities were returned.</p>}
        {candidates.map((candidate: any, index: number) => (
          <button
            type="button"
            className={`match-flow-candidate${
              (selectedCandidates[activeStep] ?? 0) === index ? ' selected' : ''
            }`}
            key={`${candidate.cigar_id || candidate.cigarId || index}-${index}`}
            onClick={() => {
              if (activeStep === 'identify') setSelectedResearchCandidate(null);
              setSelectedCandidates((current) => ({
                ...current,
                [activeStep]: index,
              }));
            }}
          >
            {candidateImage(candidate) && (
              <img src={candidateImage(candidate)} alt="" />
            )}
            <span>
              <strong>{candidateLabel(candidate)}</strong>
              <small>
                {candidate.proposed && 'Proposed match · '}
                {candidate.resultType === 'consensus' &&
                  'Consensus candidate · '}
                {candidateScore(candidate) || 'Score unavailable'}
                {(candidate.brand || candidate.cigar?.brand?.name) &&
                  ` · ${candidate.brand || candidate.cigar.brand.name}`}
              </small>
              {values(candidate.evidence).length > 0 && (
                <em className="match-flow-candidate-inline-evidence">
                  {values(candidate.evidence).join(' · ')}
                </em>
              )}
            </span>
          </button>
        ))}
      </div>
      {renderWrapperAnalysis()}
    </>
  );

  const renderFingerprint = (result: any) => {
    const fingerprint = result?.fingerprint || {};
    const renderPalette = (palette: any[] = []) => (
      <div className="match-flow-fingerprint-palette">
        {palette.map((color, index) => (
          <span key={`${color.color || color.name}-${index}`}>
            <i style={{ backgroundColor: color.color || color.name }} />
            <b>{color.color || color.name || 'Unknown'}</b>
            {percentage(color.confidence ?? color.prominence) && (
              <small>{percentage(color.confidence ?? color.prominence)}</small>
            )}
            {color.finish && <small>{color.finish}</small>}
            {color.location && <small>{color.location}</small>}
          </span>
        ))}
      </div>
    );
    const fields = [
      ['Colors', fingerprint.colors],
      ['Symbols', fingerprint.symbols],
      ['Motifs', fingerprint.motifs],
      ['Objects', fingerprint.objects],
      ['Text style', fingerprint.textStyle],
      ['Monograms', fingerprint.monograms],
      ['Shape', fingerprint.shape],
    ];
    return (
      <div className="match-flow-fingerprint">
        {(fingerprint.schemaVersion || percentage(fingerprint.confidence)) && (
          <section>
            <strong>Fingerprint</strong>
            <p>
              {fingerprint.schemaVersion || 'Legacy schema'}
              {percentage(fingerprint.confidence)
                ? ` · ${percentage(fingerprint.confidence)} confidence`
                : ''}
            </p>
          </section>
        )}
        {fields.map(([label, value]) =>
          values(value).length ? (
            <section key={String(label)}>
              <strong>{label}</strong>
              <div>
                {values(value).map((item) => (
                  <span key={item}>{item}</span>
                ))}
              </div>
            </section>
          ) : null
        )}
        {fingerprint.layout && (
          <section>
            <strong>Layout</strong>
            <p>
              {Object.values(fingerprint.layout).filter(Boolean).join(' · ')}
            </p>
          </section>
        )}
        {fingerprint.bandAppearance && (
          <section className="match-flow-fingerprint-wide">
            <strong>Band appearance</strong>
            {[
              ['Background', fingerprint.bandAppearance.backgroundColors],
              ['Border', fingerprint.bandAppearance.borderColors],
              ['Accents', fingerprint.bandAppearance.accentColors],
              ['Dominant palette', fingerprint.bandAppearance.dominantPalette],
            ].map(([label, palette]: any) =>
              palette?.length ? (
                <div className="match-flow-fingerprint-detail" key={label}>
                  <small>{label}</small>
                  {renderPalette(palette)}
                </div>
              ) : null
            )}
          </section>
        )}
        {fingerprint.typography?.length > 0 && (
          <section className="match-flow-fingerprint-wide">
            <strong>Typography</strong>
            <div className="match-flow-fingerprint-typography">
              {fingerprint.typography.map((type: any, index: number) => (
                <div key={`${type.text || type.role}-${index}`}>
                  <b>{type.text || 'Unrecognized text'}</b>
                  <span>{type.role || 'unspecified role'}</span>
                  {type.color && type.color !== 'null' && (
                    <span>{type.color}</span>
                  )}
                  {type.location && <span>{type.location}</span>}
                  {percentage(type.confidence) && (
                    <small>{percentage(type.confidence)}</small>
                  )}
                </div>
              ))}
            </div>
          </section>
        )}
        {fingerprint.colorRelationships?.length > 0 && (
          <section className="match-flow-fingerprint-wide">
            <strong>Color relationships</strong>
            <div className="match-flow-fingerprint-relationships">
              {fingerprint.colorRelationships.map(
                (relationship: any, index: number) => (
                  <span key={`${relationship.role}-${index}`}>
                    <b>
                      {relationship.foreground || 'Foreground'} on{' '}
                      {relationship.background || 'background'}
                    </b>
                    <small>
                      {[
                        relationship.role,
                        relationship.contrast,
                        percentage(relationship.confidence),
                      ]
                        .filter(Boolean)
                        .join(' · ')}
                    </small>
                  </span>
                )
              )}
            </div>
          </section>
        )}
        {fingerprint.bands?.length > 0 && (
          <section className="match-flow-fingerprint-wide">
            <strong>Detected bands</strong>
            <div className="match-flow-detected-bands">
              {fingerprint.bands.map((band: any, index: number) => (
                <div key={`${band.role}-${index}`}>
                  <b>{band.role || `Band ${index + 1}`}</b>
                  {band.appearance?.dominantPalette?.length > 0 &&
                    renderPalette(band.appearance.dominantPalette)}
                  {band.typography?.length > 0 && (
                    <small>
                      Text:{' '}
                      {band.typography
                        .map((type: any) => type.text)
                        .filter(Boolean)
                        .join(', ')}
                    </small>
                  )}
                </div>
              ))}
            </div>
          </section>
        )}
        {(fingerprint.description || fingerprint.artwork) && (
          <section className="match-flow-fingerprint-wide">
            <strong>Description</strong>
            <p>{fingerprint.description || fingerprint.artwork}</p>
          </section>
        )}
      </div>
    );
  };

  const renderText = (result: any) => (
    <div className="match-flow-text-result">
      <section>
        <strong>Detected text</strong>
        <pre>{result.rawText || 'No text detected'}</pre>
      </section>
      {result.normalizedText && (
        <section>
          <strong>Normalized</strong>
          <p>{result.normalizedText}</p>
        </section>
      )}
      {[
        ['Tokens', result.tokens],
        ['Brands', result.brandCandidates || result.brand_candidates],
        ['Names', result.nameCandidates || result.name_candidates],
        ['Series', result.seriesCandidates || result.series_candidates],
        ['Aliases', result.aliases],
        ['Tags', result.tags || result.matchingTags],
        ['Barcodes', result.barcodeCandidates || result.barcode_candidates],
        ['Other clues', result.otherClues || result.other_clues],
      ].map(([label, value]) =>
        values(value).length ? (
          <section key={String(label)}>
            <strong>{label}</strong>
            <div className="match-flow-chips">
              {values(value).map((item) => (
                <span key={item}>{item}</span>
              ))}
            </div>
          </section>
        ) : null
      )}
      {percentage(result.confidence) && (
        <p>Confidence {percentage(result.confidence)}</p>
      )}
    </div>
  );

  const importHypothesis = async (
    hypothesis: any,
    index: number,
    research: any
  ) => {
    if (hypothesisImports[index]?.status === 'importing') return;
    setHypothesisImports((current) => ({
      ...current,
      [index]: { status: 'importing' },
    }));
    try {
      const sourceUrl =
        hypothesis.sourceUrls?.[0] || research.sources?.[0]?.url;
      const enrichment = await enrichCigarHypothesis({
        ...(sourceUrl ? { sourceUrl } : {}),
        cigar: {
          brand: hypothesis.brand || null,
          name: hypothesis.name || null,
          line: hypothesis.line || null,
          aliases: hypothesis.aliases || [],
          countryOfOrigin: hypothesis.countryOfOrigin || null,
          wrapper: hypothesis.wrapper || null,
          binder: hypothesis.binder || null,
          filler: hypothesis.filler || null,
          strength: hypothesis.strength || null,
        },
      });
      const enriched = enrichment.cigar || enrichment;
      const editableValue = (value: unknown) =>
        Array.isArray(value) ? value.join(', ') : value || '';
      const brand = enriched.brand || hypothesis.brand || '';
      setEnrichmentPreview({
        index,
        hypothesis,
        sourceUrl: sourceUrl || '',
        brand: brand ? { value: brand, label: brand } : null,
        data: {
          ...enriched,
          brand,
          name: enriched.name || hypothesis.name || '',
          line: enriched.line || hypothesis.line || '',
          aliases: editableValue(enriched.aliases || hypothesis.aliases),
          countryOfOrigin: editableValue(
            enriched.countryOfOrigin ||
              enriched.origin ||
              hypothesis.countryOfOrigin
          ),
          wrapper: editableValue(enriched.wrapper),
          binder: editableValue(enriched.binder),
          filler: editableValue(enriched.filler),
          strength: editableValue(enriched.strength),
        },
      });
      setEnrichmentError('');
    } catch (error) {
      setHypothesisImports((current) => ({
        ...current,
        [index]: { status: 'failed', error: errorMessage(error) },
      }));
    }
  };

  const closeEnrichmentPreview = () => {
    if (savingEnrichment || reimportingEnrichment) return;
    if (enrichmentPreview) {
      setHypothesisImports((current) => {
        const next = { ...current };
        delete next[enrichmentPreview.index];
        return next;
      });
    }
    setEnrichmentPreview(null);
    setEnrichmentError('');
  };

  const reimportEnrichment = async () => {
    if (!enrichmentPreview || reimportingEnrichment) return;
    const { brand, data, sourceUrl } = enrichmentPreview;
    if (!sourceUrl.trim()) {
      setEnrichmentError('Enter a source URL before re-importing.');
      return;
    }
    setReimportingEnrichment(true);
    setEnrichmentError('');
    try {
      const enrichment = await enrichCigarHypothesis({
        sourceUrl: sourceUrl.trim(),
        cigar: {
          brand: brand?.label || data.brand || null,
          name: data.name || null,
          line: data.line || null,
          aliases: String(data.aliases || '')
            .split(',')
            .map((alias) => alias.trim())
            .filter(Boolean),
          countryOfOrigin: data.countryOfOrigin || null,
          wrapper: data.wrapper || null,
          binder: data.binder || null,
          filler: data.filler || null,
          strength: data.strength || null,
        },
      });
      const refreshed = enrichment.cigar || enrichment;
      const editableValue = (value: unknown) =>
        Array.isArray(value) ? value.join(', ') : value || '';
      const refreshedBrand = refreshed.brand || brand?.label || '';
      let nextBrand: BrandOption | null = null;
      if (brand?.label === refreshedBrand) {
        nextBrand = brand;
      } else if (refreshedBrand) {
        nextBrand = { value: refreshedBrand, label: refreshedBrand };
      }
      setEnrichmentPreview((current) =>
        current
          ? {
              ...current,
              sourceUrl: sourceUrl.trim(),
              brand: nextBrand,
              data: {
                ...current.data,
                ...refreshed,
                brand: refreshedBrand,
                name: refreshed.name || current.data.name || '',
                line: refreshed.line || '',
                aliases: editableValue(refreshed.aliases),
                countryOfOrigin: editableValue(
                  refreshed.countryOfOrigin || refreshed.origin
                ),
                wrapper: editableValue(refreshed.wrapper),
                binder: editableValue(refreshed.binder),
                filler: editableValue(refreshed.filler),
                strength: editableValue(refreshed.strength),
              },
            }
          : current
      );
    } catch (error) {
      setEnrichmentError(errorMessage(error));
    } finally {
      setReimportingEnrichment(false);
    }
  };

  const loadBrandOptions = async (query: string): Promise<BrandOption[]> => {
    const brands: any[] = await getBrands(query);
    return brands.map((brand) => ({
      value: brand.id,
      label: brand.name,
    }));
  };

  const saveEnrichedCigar = async () => {
    if (!enrichmentPreview || savingEnrichment) return;
    const { brand, data, index } = enrichmentPreview;
    if (!brand?.label || !String(data.name || '').trim()) {
      setEnrichmentError('Brand and name are required.');
      return;
    }
    setSavingEnrichment(true);
    setEnrichmentError('');
    try {
      const brandId = brand.__isNew__ ? NaN : Number(brand.value);
      const origin = String(data.countryOfOrigin || '').trim() || null;
      const createdCigar = await postCigar({
        ...data,
        brand: brand.label,
        brand_id: Number.isInteger(brandId) ? brandId : undefined,
        name: String(data.name).trim(),
        line: String(data.line || '').trim() || null,
        aliases: String(data.aliases || '')
          .split(',')
          .map((alias) => alias.trim())
          .filter(Boolean),
        origin,
        countryOfOrigin: undefined,
        wrapper: String(data.wrapper || '').trim() || null,
        binder: String(data.binder || '').trim() || null,
        filler: String(data.filler || '').trim() || null,
        strength: String(data.strength || '').trim() || null,
        pending: false,
        custom: false,
        cuban: origin === 'Cuba',
      });
      if (!createdCigar?.id) {
        throw new Error('The admin server did not return a cigar ID.');
      }
      setHypothesisImports((current) => ({
        ...current,
        [index]: { status: 'complete', cigar: createdCigar },
      }));
      setEnrichmentPreview(null);
    } catch (error) {
      setEnrichmentError(errorMessage(error));
    } finally {
      setSavingEnrichment(false);
    }
  };

  const renderResearch = (result: any) => {
    const research = result?.research;
    if (!research?.attempted) return null;
    const hypotheses = research.hypotheses || [];
    const catalogCandidates = result.hydratedResearchCandidates?.length
      ? result.hydratedResearchCandidates
      : research.catalogCandidates || [];

    return (
      <div className="match-flow-research">
        <div className="match-flow-section-heading">
          <div>
            <h6>Research findings</h6>
            <span>
              {research.mode || researchMode}
              {` · ${research.profile || research.researchProfile || researchProfile}`}
              {research.model ? ` · ${research.model}` : ''}
              {research.decisionReason
                ? ` · ${research.decisionReason.split('_').join(' ')}`
                : ''}
            </span>
          </div>
        </div>
        {research.summary &&
          renderEvidence(
            <p className="match-flow-research-summary">{research.summary}</p>,
            'View research summary'
          )}
        {hypotheses.length > 0 && (
          <section>
            <div className="match-flow-section-heading">
              <div>
                <h6>Potential new catalog entries</h6>
                <span>
                  Research hypotheses that are not linked to a database cigar
                </span>
              </div>
            </div>
            <div className="match-flow-hypotheses">
              {hypotheses.map((hypothesis: any, index: number) => (
                <article
                  key={`${hypothesis.brand}-${hypothesis.name}-${index}`}
                >
                  <header>
                    <div>
                      <strong>
                        {[hypothesis.brand, hypothesis.name]
                          .filter(Boolean)
                          .join(' · ') || 'Unidentified cigar'}
                      </strong>
                      {hypothesis.line && <small>{hypothesis.line}</small>}
                    </div>
                    {percentage(hypothesis.confidence) && (
                      <b>{percentage(hypothesis.confidence)}</b>
                    )}
                  </header>
                  {(hypothesis.reasoning ||
                    values(hypothesis.evidence).length > 0 ||
                    values(hypothesis.visualTags).length > 0) &&
                    renderEvidence(
                      <>
                        {hypothesis.reasoning && <p>{hypothesis.reasoning}</p>}
                        {values(hypothesis.evidence).length > 0 && (
                          <ul>
                            {values(hypothesis.evidence).map((item) => (
                              <li key={item}>{item}</li>
                            ))}
                          </ul>
                        )}
                        {values(hypothesis.visualTags).length > 0 && (
                          <div className="match-flow-chips">
                            {values(hypothesis.visualTags).map((tag) => (
                              <span key={tag}>{tag}</span>
                            ))}
                          </div>
                        )}
                      </>,
                      'View hypothesis evidence'
                    )}
                  <div className="match-flow-hypothesis-actions">
                    {hypothesisImports[index]?.status === 'complete' ? (
                      <>
                        <span>
                          Added as cigar #{hypothesisImports[index].cigar.id}
                        </span>
                        <Button
                          size="sm"
                          color="success"
                          disabled={isAccepting}
                          onClick={() =>
                            acceptCandidate(
                              hypothesisImports[index].cigar,
                              'identify',
                              percentage(hypothesis.confidence)
                            )
                          }
                        >
                          {isAccepting ? (
                            <>
                              <Spinner size="sm" /> Matching…
                            </>
                          ) : (
                            acceptButtonLabel
                          )}
                        </Button>
                      </>
                    ) : (
                      <Button
                        size="sm"
                        color="primary"
                        disabled={
                          hypothesisImports[index]?.status === 'importing'
                        }
                        onClick={() =>
                          importHypothesis(hypothesis, index, research)
                        }
                      >
                        {hypothesisImports[index]?.status === 'importing' ? (
                          <>
                            <Spinner size="sm" /> Importing…
                          </>
                        ) : (
                          'Import'
                        )}
                      </Button>
                    )}
                  </div>
                  {hypothesisImports[index]?.status === 'failed' && (
                    <p className="match-flow-hypothesis-error">
                      {hypothesisImports[index].error}
                    </p>
                  )}
                </article>
              ))}
            </div>
          </section>
        )}
        {catalogCandidates.length > 0 && (
          <section>
            <div className="match-flow-section-heading">
              <div>
                <h6>Research catalog candidates</h6>
                <span>Existing database cigars found during research</span>
              </div>
            </div>
            <div className="match-flow-research-candidates">
              {catalogCandidates.map((candidate: any, index: number) => (
                <button
                  type="button"
                  className={
                    selectedResearchCandidate === index ? 'selected' : ''
                  }
                  key={`${candidate.cigarId || candidate.cigar_id || index}-${index}`}
                  onClick={() => {
                    setSelectedResearchCandidate(index);
                    setSelectedCandidates((current) => ({
                      ...current,
                      identify: -1,
                    }));
                  }}
                >
                  {candidateBandImage(candidate) && (
                    <img src={candidateBandImage(candidate)} alt="" />
                  )}
                  <span>
                    <strong>{candidateLabel(candidate)}</strong>
                    <small>
                      {candidateScore(candidate) || 'Score unavailable'}
                      {candidate.brand ? ` · ${candidate.brand}` : ''}
                    </small>
                    {values(candidate.matchedBy).length > 0 && (
                      <em className="match-flow-candidate-inline-evidence">
                        Matched by {values(candidate.matchedBy).join(' · ')}
                      </em>
                    )}
                  </span>
                </button>
              ))}
            </div>
            {catalogCandidates.some(
              (candidate: any) => values(candidate.matchedBy).length > 0
            ) &&
              renderEvidence(
                <div className="match-flow-mobile-candidate-evidence">
                  <strong>Research candidate evidence</strong>
                  {catalogCandidates
                    .filter(
                      (candidate: any) => values(candidate.matchedBy).length > 0
                    )
                    .map((candidate: any, index: number) => (
                      <div
                        key={`${candidateCigarId(candidate) ?? index}-research-evidence`}
                      >
                        <b>{candidateLabel(candidate)}</b>
                        <span>
                          Matched by {values(candidate.matchedBy).join(' · ')}
                        </span>
                      </div>
                    ))}
                </div>,
                'View research candidate evidence'
              )}
          </section>
        )}
        {research.sources?.length > 0 && (
          <>
            {renderEvidence(
              <section className="match-flow-research-sources">
                <strong>Sources</strong>
                {research.sources.map((sourceItem: any) => (
                  <a
                    href={sourceItem.url}
                    target="_blank"
                    rel="noreferrer"
                    key={sourceItem.url}
                  >
                    {sourceItem.title || sourceItem.url}
                  </a>
                ))}
              </section>,
              'View research sources'
            )}
          </>
        )}
      </div>
    );
  };

  const renderResult = () => {
    if (stage.status === 'processing') {
      return (
        <div className="match-flow-processing">
          <Spinner />
          <strong>Processing…</strong>
          <span>Completed results remain available from the steps above.</span>
        </div>
      );
    }
    if (stage.status === 'failed') {
      return (
        <div className="match-flow-error">
          <strong>Stage failed</strong>
          <span>{stage.error}</span>
        </div>
      );
    }
    if (stage.status === 'idle') {
      return (
        <div className="match-flow-empty">
          <strong>This stage has not run.</strong>
          <span>Run it when you are ready to inspect its output.</span>
        </div>
      );
    }
    if (stage.status === 'skipped') {
      let skippedLabel = `${STEPS[activeIndex].label} skipped`;
      let skippedMessage =
        'Subsequent stages use the currently selected working image.';

      if (
        activeStep === 'similar' &&
        stage.skipReason === 'isolation_skipped_similarity_requires_band_crop'
      ) {
        skippedLabel = 'Similarity skipped';
        skippedMessage =
          'Similarity requires a fully isolated band, so this stage was skipped.';
      }

      return (
        <div className="match-flow-empty">
          <strong>{skippedLabel}</strong>
          <span>{skippedMessage}</span>
        </div>
      );
    }
    if (activeStep === 'image') return null;
    if (activeStep === 'detect') {
      const detectedCigars: DetectedCigar[] = stage.result?.cigars || [];
      return (
        <div className="match-flow-detect-result">
          <div className="match-flow-section-heading">
            <div>
              <h6>
                {stage.result?.detectedCount ?? detectedCigars.length}{' '}
                {detectedCigars.length === 1
                  ? 'cigar detected'
                  : 'cigars detected'}
              </h6>
              <span>Select one crop to continue the single-cigar flow.</span>
            </div>
          </div>
          {values(stage.result?.warnings).map((warning) => (
            <div className="alert alert-warning" key={warning}>
              {warning}
            </div>
          ))}
          {detectedCigars.length > 0 && (
            <div className="match-flow-detections">
              {detectedCigars.map((detection, index) => {
                const { bounds } = detection;
                const bandVisibility =
                  detection.bandVisibility ?? detection.bandVisible;
                return (
                  <article
                    className={
                      selectedDetectionId === detection.detectionId
                        ? 'selected'
                        : ''
                    }
                    key={detection.detectionId || index}
                  >
                    <img
                      src={detection.image}
                      alt={`Detected cigar ${index + 1}`}
                    />
                    <div>
                      <strong>
                        Cigar {index + 1} —{' '}
                        {percentage(detection.confidence) || '—'}
                      </strong>
                      {detection.partial && <span>Partial cigar</span>}
                      {detection.orientation?.direction && (
                        <span>{detection.orientation.direction}</span>
                      )}
                      {bandVisibility !== undefined && (
                        <span>
                          Band visibility: {formatEvidenceValue(bandVisibility)}
                        </span>
                      )}
                      {bounds && (
                        <small>
                          Bounds: {bounds.x}, {bounds.y} · {bounds.width} ×{' '}
                          {bounds.height}
                        </small>
                      )}
                      <Button
                        size="sm"
                        color="primary"
                        disabled={
                          selectedDetectionId === detection.detectionId ||
                          automaticIsActive
                        }
                        onClick={() =>
                          selectWorkingImage(
                            detection.image,
                            'detected',
                            detection.detectionId
                          )
                        }
                      >
                        {selectedDetectionId === detection.detectionId
                          ? 'Using detected cigar'
                          : 'Use detected cigar'}
                      </Button>
                    </div>
                  </article>
                );
              })}
            </div>
          )}
          {renderEvidence(
            renderMeta(stage.result, 'Free · Local vision'),
            'View detection metadata'
          )}
        </div>
      );
    }
    if (activeStep === 'preprocess') {
      const { result } = stage;
      return (
        <>
          {renderEvidence(
            <>
              <div className="match-flow-preprocess-output">
                {result?.image ? (
                  <img
                    src={result.image}
                    alt="Automatically isolated cigar band"
                  />
                ) : (
                  <div>No safe crop was produced.</div>
                )}
                <div>
                  <strong>
                    Confidence {percentage(result?.confidence) || '—'}
                  </strong>
                  {result?.region && (
                    <p>
                      Region: {result.region.x}, {result.region.y} ·{' '}
                      {result.region.width} × {result.region.height}
                    </p>
                  )}
                  <p>{values(result?.transformations).join(' · ')}</p>
                  {values(result?.notes).map((note) => (
                    <p key={note}>{note}</p>
                  ))}
                  {result?.uploadError && (
                    <p className="text-danger">
                      Processed-image upload failed: {result.uploadError}
                    </p>
                  )}
                </div>
              </div>
              {renderMeta(result, 'Free · Local')}
            </>,
            'View isolation evidence'
          )}{' '}
          {result?.publicImageUrl && (
            <Button
              color="primary"
              onClick={() =>
                selectWorkingImage(result.publicImageUrl, 'preprocessed')
              }
            >
              Use processed image
            </Button>
          )}
        </>
      );
    }
    if (activeStep === 'text')
      return (
        <>
          <div className="match-flow-section-heading">
            <div>
              <h6>Text catalog candidates</h6>
              <span>
                Existing database cigars matched from the recognized text
              </span>
            </div>
          </div>
          {renderCandidates()}
          {renderEvidence(
            <>
              {renderText(stage.result)}
              {renderCandidateEvidence()}
              {renderMeta(stage.result, 'Paid · Vision')}
            </>,
            'View text evidence'
          )}
        </>
      );
    if (activeStep === 'fingerprint')
      return renderEvidence(
        <>
          {renderFingerprint(stage.result)}
          {renderMeta(stage.result, 'Paid · Vision')}
        </>,
        'View fingerprint evidence'
      );
    if (activeStep === 'identify')
      return (
        <>
          {renderIdentifyResultSummary()}
          <div className="match-flow-section-heading">
            <div>
              <h6>Match and suggestions</h6>
              <span>
                Selectable match, suggestion, and consensus results linked to
                existing database cigars
              </span>
            </div>
          </div>
          {renderCandidates()}
          {renderResearch(stage.result)}
          {renderEvidence(
            <>
              {renderCandidateEvidence()}
              {renderMeta(stage.result, STEPS[activeIndex].cost)}
            </>
          )}
        </>
      );
    return (
      <>
        {renderCandidates()}
        {renderEvidence(
          <>
            {renderCandidateEvidence()}
            {renderMeta(stage.result, STEPS[activeIndex].cost)}
          </>
        )}
      </>
    );
  };

  const identifyOptions: Array<{
    key: IdentifyStageKey;
    label: string;
    available: boolean;
  }> = [
    {
      key: 'similar',
      label: 'Similar',
      available: Boolean(stages.similar.result),
    },
    {
      key: 'text',
      label: 'Text',
      available: Boolean(stages.text.result),
    },
    {
      key: 'fingerprint',
      label: 'Fingerprint',
      available: Boolean(stages.fingerprint.result),
    },
    {
      key: 'recognize',
      label: 'Recognize',
      available: Boolean(stages.fingerprintRecognition.result),
    },
    {
      key: 'analyze',
      label: 'Analyze',
      available: wrapperAnalyses.some(
        (analysis) =>
          analysis.status === 'complete' && analysis.imageUrl === workingImage
      ),
    },
  ];

  const latestAutoDecision = autoRun.decisionLog.reduce(
    (latest, decision) => ({ ...latest, [decision.step]: decision }),
    {} as Partial<Record<AutoStepKey, AutoDecision>>
  );
  const hasCompletedRecognitionStep = AUTO_STEP_ORDER.some(
    (key) => stages[key].status === 'complete'
  );
  const selectedDetection: DetectedCigar | undefined = (
    stages.detect.result?.cigars || []
  ).find(
    (detection: DetectedCigar) => detection.detectionId === selectedDetectionId
  );
  let automaticStatusMessage =
    'Every run, skip, failure, and stopping decision is retained below.';
  if (automaticIsActive) {
    const activeLabel = STEPS.find(
      ({ key }) => key === autoRun.activeStep
    )?.label;
    automaticStatusMessage = `Processing ${activeLabel || 'next stage'}…`;
  }
  if (autoRun.status === 'waiting_for_selection') {
    automaticStatusMessage =
      'Choose a detected cigar, then run automatically from here.';
  }

  const renderAutomaticStatus = () => {
    if (
      autoRun.status === 'idle' &&
      autoRun.decisionLog.length === 0 &&
      !autoRun.summary
    ) {
      return null;
    }
    return (
      <section className="match-flow-auto-status">
        <div className="match-flow-section-heading">
          <div>
            <h6>
              {autoRun.status === 'waiting_for_selection'
                ? 'Automatic run paused'
                : 'Automatic orchestration'}
            </h6>
            <span>{automaticStatusMessage}</span>
          </div>
          {automaticIsActive && <Spinner size="sm" />}
        </div>
        {autoRun.summary && (
          <div className="match-flow-auto-summary">
            <strong>Autonomous Match Complete</strong>
            {autoRun.summary.label ? (
              <h5>{autoRun.summary.label}</h5>
            ) : (
              <h5>No confident match found.</h5>
            )}
            {autoRun.summary.confidence !== null && (
              <span>Confidence: {percentage(autoRun.summary.confidence)}</span>
            )}
            <span>
              Final stage:{' '}
              {STEPS.find(({ key }) => key === autoRun.summary?.finalStep)
                ?.label || autoRun.summary.finalStep}
            </span>
            <span>
              {autoRun.summary.executed} executed · {autoRun.summary.skipped}{' '}
              skipped ·{' '}
              {duration({ meta: { durationMs: autoRun.summary.durationMs } })}
            </span>
            <span>Reason: {formatDecisionReason(autoRun.summary.reason)}</span>
            {!autoRun.summary.confident && (
              <span>Recommendation: Manual review required.</span>
            )}
          </div>
        )}
        {autoRun.error && (
          <div className="alert alert-danger mb-0">{autoRun.error}</div>
        )}
        {autoRun.decisionLog.length > 0 && (
          <details className="match-flow-auto-log">
            <summary>Decision log ({autoRun.decisionLog.length})</summary>
            <ol>
              {autoRun.decisionLog.map((decision) => (
                <li key={decision.id}>
                  <strong>
                    {STEPS.find(({ key }) => key === decision.step)?.label ||
                      decision.step}
                  </strong>
                  <span className={`action-${decision.action}`}>
                    {decision.action.split('_').join(' ')}
                  </span>
                  <small>{formatDecisionReason(decision.reason)}</small>
                  {decision.durationMs !== undefined && (
                    <small>
                      {duration({ meta: { durationMs: decision.durationMs } })}
                    </small>
                  )}
                  {decision.confidence !== undefined && (
                    <small>{percentage(decision.confidence)}</small>
                  )}
                  {decision.detail && <small>{decision.detail}</small>}
                </li>
              ))}
            </ol>
          </details>
        )}
      </section>
    );
  };

  if (!source) return null;

  return (
    <>
      <Modal
        isOpen={isOpen}
        toggle={automaticIsActive ? undefined : onClose}
        className="match-flow-dialog"
        fade
      >
        <div className="modal-header match-flow-header">
          <div>
            <h5 className="modal-title">Cigar Match Flow</h5>
            <small>
              {source.sourceType.replace('_', ' ')}
              {source.sourceId !== undefined ? ` · ${source.sourceId}` : ''}
            </small>
          </div>
          <button
            type="button"
            className="close"
            onClick={onClose}
            disabled={automaticIsActive}
          >
            ×
          </button>
        </div>
        <div className="match-flow-steps">
          {STEPS.map((item, index) => {
            const { status } = stages[item.key];
            const autoDecision =
              item.key === 'image' || status === 'idle'
                ? undefined
                : latestAutoDecision[item.key];
            return (
              <React.Fragment key={item.key}>
                <button
                  type="button"
                  ref={item.key === activeStep ? activeStepButton : null}
                  className={`match-flow-step ${status}${item.key === activeStep ? ' active' : ''}`}
                  onClick={() => setActiveStep(item.key)}
                >
                  <span>
                    {status === 'processing' ? (
                      <Spinner size="sm" />
                    ) : (
                      index + 1
                    )}
                  </span>
                  <strong>{item.label}</strong>
                  <small>{item.cost}</small>
                  {autoDecision && (
                    <em className={`decision-${autoDecision.action}`}>
                      {autoDecision.action === 'skipped' ? '○' : '✓'}{' '}
                      {formatDecisionReason(autoDecision.reason)}
                      {autoDecision.confidence !== undefined
                        ? ` · ${percentage(autoDecision.confidence)}`
                        : ''}
                    </em>
                  )}
                </button>
                {index < STEPS.length - 1 && <i>→</i>}
              </React.Fragment>
            );
          })}
        </div>
        <div className="modal-body match-flow-body">
          <aside className="match-flow-images">
            <div>
              <span>Original upload</span>
              <img src={sourceImage} alt="Original cigar" />
            </div>
            {source.image !== sourceImage && (
              <div>
                <span>Manual crop</span>
                <img src={source.image} alt="Manually cropped cigar" />
              </div>
            )}
            {selectedDetection && (
              <div>
                <span>Detected cigar · {selectedDetection.detectionId}</span>
                <img
                  src={selectedDetection.image}
                  alt="Selected detected cigar"
                />
              </div>
            )}
            <div className="match-flow-working">
              <span>Working image · {workingImageType}</span>
              <img src={workingImage} alt="Current recognition input" />
            </div>
            <div className="match-flow-image-buttons">
              <Button
                size="sm"
                disabled={workingImage === sourceImage || automaticIsActive}
                onClick={() => selectWorkingImage(sourceImage, 'original')}
              >
                Use original
              </Button>
              {source.image !== sourceImage && (
                <Button
                  size="sm"
                  disabled={workingImage === source.image || automaticIsActive}
                  onClick={() => selectWorkingImage(source.image, 'manual')}
                >
                  Use manual crop
                </Button>
              )}
              {selectedDetection && (
                <Button
                  size="sm"
                  disabled={
                    workingImage === selectedDetection.image ||
                    automaticIsActive
                  }
                  onClick={() =>
                    selectWorkingImage(
                      selectedDetection.image,
                      'detected',
                      selectedDetection.detectionId
                    )
                  }
                >
                  Use detected cigar
                </Button>
              )}
            </div>
          </aside>
          <main className="match-flow-content">
            {renderAutomaticStatus()}
            <div className="match-flow-stage-heading">
              <div>
                <h5>{STEPS[activeIndex].label}</h5>
                <span>{STEPS[activeIndex].cost}</span>
              </div>
              {activeStep !== 'image' && (
                <div className="match-flow-stage-actions">
                  {(activeStep === 'detect' || activeStep === 'preprocess') && (
                    <Button
                      size="sm"
                      color="light"
                      disabled={
                        stage.status === 'processing' || automaticIsActive
                      }
                      onClick={
                        activeStep === 'detect' ? skipDetect : skipPreprocess
                      }
                    >
                      Skip
                    </Button>
                  )}
                  <Button
                    size="sm"
                    disabled={
                      stage.status === 'processing' ||
                      automaticIsActive ||
                      similarBlockedByIsolation
                    }
                    onClick={() => {
                      clearDownstream(activeStep);
                      runStage(activeStep);
                    }}
                  >
                    {stage.status === 'idle' ? 'Run Step' : 'Run Again'}
                  </Button>
                </div>
              )}
            </div>
            {activeStep === 'identify' && (
              <div className="match-flow-identify-controls">
                <fieldset className="match-flow-identify-options">
                  <legend>Include previous results</legend>
                  {identifyOptions.map(({ key, label, available }) => (
                    <label key={key}>
                      <input
                        type="checkbox"
                        checked={identifySelection[key] && available}
                        disabled={!available || stage.status === 'processing'}
                        onChange={(event) =>
                          setIdentifySelection((current) => ({
                            ...current,
                            [key]: event.target.checked,
                          }))
                        }
                      />
                      <span>{label}</span>
                      {!available && <small>Not run</small>}
                    </label>
                  ))}
                </fieldset>
                <fieldset className="match-flow-identify-options">
                  <legend>Research strategy</legend>
                  {(
                    [
                      'auto',
                      'open_world',
                      'catalog_guided',
                    ] as IdentifyResearchMode[]
                  ).map((mode) => (
                    <label key={mode}>
                      <input
                        type="radio"
                        name="match-flow-research-mode"
                        value={mode}
                        checked={researchMode === mode}
                        disabled={stage.status === 'processing'}
                        onChange={() => setResearchMode(mode)}
                      />
                      <span>{mode.split('_').join(' ')}</span>
                    </label>
                  ))}
                </fieldset>
                <fieldset className="match-flow-identify-options">
                  <legend>Research profile</legend>
                  {(['quick', 'thorough'] as IdentifyResearchProfile[]).map(
                    (mode) => (
                      <label key={mode}>
                        <input
                          type="radio"
                          name="match-flow-research-profile"
                          value={mode}
                          checked={researchProfile === mode}
                          disabled={stage.status === 'processing'}
                          onChange={() => setResearchProfile(mode)}
                        />
                        <span>{mode}</span>
                      </label>
                    )
                  )}
                </fieldset>
              </div>
            )}
            {activeStep === 'image' && (
              <div className="match-flow-image-start">
                <h4>Choose how to begin</h4>
                <p>
                  Keep the current image selected, optionally detect an
                  individual cigar and isolate its band, or skip directly to
                  text recognition.
                </p>
                <div>
                  <Button
                    color="primary"
                    disabled={automaticIsActive}
                    onClick={() => runStage('detect')}
                  >
                    Run detection
                  </Button>
                  <Button
                    disabled={automaticIsActive}
                    onClick={skipDetectAndRunPreprocess}
                  >
                    Skip detect · run isolation
                  </Button>
                  <Button disabled={automaticIsActive} onClick={skipToText}>
                    Skip to text
                  </Button>
                </div>
              </div>
            )}
            {renderResult()}
            {stage.result && (
              <details className="match-flow-raw">
                <summary>Raw response</summary>
                <pre>{JSON.stringify(stage.result, null, 2)}</pre>
              </details>
            )}
          </main>
        </div>
        <div className="modal-footer match-flow-footer">
          <div>
            {accepted ? (
              <div className="match-flow-accepted">
                <strong>Accepted · {candidateLabel(accepted.candidate)}</strong>
                <span>
                  Matched at{' '}
                  {STEPS.find(({ key }) => key === accepted.step)?.label}
                  {accepted.confidence ? ` · ${accepted.confidence}` : ''}
                </span>
              </div>
            ) : (
              <span className="text-muted">
                No result accepted for this run.
              </span>
            )}
            {(runUsage.tokens > 0 || runUsage.cost > 0) && (
              <span className="match-flow-run-usage">
                Run total: {runUsage.tokens.toLocaleString()} tokens ·{' '}
                {formatCost(runUsage.cost)} estimated
              </span>
            )}
            {acceptError && (
              <p className="match-flow-accept-error">{acceptError}</p>
            )}
          </div>
          <div>
            {automaticIsActive ? (
              <>
                <Button color="danger" onClick={stopAutomaticRun}>
                  Stop
                </Button>
                <span className="match-flow-auto-running">
                  <Spinner size="sm" /> Running Automatically…
                </span>
              </>
            ) : (
              <>
                <Button color="primary" onClick={() => runAutomatically(false)}>
                  Run Automatically
                </Button>
                {hasCompletedRecognitionStep && (
                  <Button onClick={() => runAutomatically(true)}>
                    Run Automatically From Here
                  </Button>
                )}
              </>
            )}
            {selectedCandidate && activeStep !== 'fingerprint' && (
              <Button
                color="success"
                disabled={isAccepting || automaticIsActive}
                onClick={() => acceptCandidate()}
              >
                {isAccepting && <Spinner size="sm" />}
                {isAccepting ? 'Matching…' : acceptButtonLabel}
              </Button>
            )}
            {activeStep !== 'image' &&
              activeIndex < STEPS.length - 1 &&
              stage.status !== 'processing' &&
              !automaticIsActive && (
                <Button color="primary" onClick={nextStep}>
                  Run Next Step
                </Button>
              )}
            <Button
              color="light"
              onClick={onClose}
              disabled={automaticIsActive}
            >
              Close
            </Button>
          </div>
        </div>
      </Modal>
      <Modal
        isOpen={Boolean(enrichmentPreview)}
        toggle={closeEnrichmentPreview}
        className="match-flow-enrichment-dialog"
      >
        <div className="modal-header">
          <div>
            <h5 className="modal-title">Review enriched cigar</h5>
            <small className="text-muted">
              Confirm or correct the AI-provided details before adding this
              cigar.
            </small>
          </div>
          <button
            type="button"
            className="close"
            onClick={closeEnrichmentPreview}
            disabled={savingEnrichment || reimportingEnrichment}
          >
            ×
          </button>
        </div>
        {enrichmentPreview && (
          <div className="modal-body match-flow-enrichment-form">
            <div className="match-flow-enrichment-source match-flow-enrichment-wide">
              <label>
                <span>Source URL</span>
                <input
                  className="form-control"
                  value={enrichmentPreview.sourceUrl}
                  disabled={reimportingEnrichment || savingEnrichment}
                  onChange={(event) =>
                    setEnrichmentPreview((current) =>
                      current
                        ? { ...current, sourceUrl: event.target.value }
                        : current
                    )
                  }
                />
              </label>
              <Button
                color="secondary"
                onClick={reimportEnrichment}
                disabled={reimportingEnrichment || savingEnrichment}
              >
                {reimportingEnrichment ? (
                  <>
                    <Spinner size="sm" /> Re-importing…
                  </>
                ) : (
                  'Re-import'
                )}
              </Button>
            </div>
            <label>
              <span>Brand</span>
              <AsyncCreatableSelect
                value={enrichmentPreview.brand}
                onChange={(brand) =>
                  setEnrichmentPreview((current) =>
                    current
                      ? { ...current, brand: brand as BrandOption | null }
                      : current
                  )
                }
                loadOptions={loadBrandOptions}
                defaultOptions
                isClearable
                isDisabled={savingEnrichment || reimportingEnrichment}
                placeholder="Search brands…"
              />
            </label>
            {[
              ['name', 'Name'],
              ['line', 'Line'],
              ['countryOfOrigin', 'Country of origin'],
              ['wrapper', 'Wrapper'],
              ['binder', 'Binder'],
              ['filler', 'Filler'],
              ['strength', 'Strength'],
              ['aliases', 'Aliases (comma separated)'],
            ].map(([field, label]) => (
              <label key={field}>
                <span>{label}</span>
                <input
                  className="form-control"
                  value={enrichmentPreview.data[field] || ''}
                  disabled={savingEnrichment || reimportingEnrichment}
                  onChange={(event) =>
                    setEnrichmentPreview((current) =>
                      current
                        ? {
                            ...current,
                            data: {
                              ...current.data,
                              [field]: event.target.value,
                            },
                          }
                        : current
                    )
                  }
                />
              </label>
            ))}
            <label className="match-flow-enrichment-wide">
              <span>Description</span>
              <textarea
                className="form-control"
                rows={4}
                value={enrichmentPreview.data.description || ''}
                disabled={savingEnrichment || reimportingEnrichment}
                onChange={(event) =>
                  setEnrichmentPreview((current) =>
                    current
                      ? {
                          ...current,
                          data: {
                            ...current.data,
                            description: event.target.value,
                          },
                        }
                      : current
                  )
                }
              />
            </label>
            {enrichmentError && (
              <div className="alert alert-danger match-flow-enrichment-wide mb-0">
                {enrichmentError}
              </div>
            )}
          </div>
        )}
        <div className="modal-footer">
          <Button
            color="light"
            onClick={closeEnrichmentPreview}
            disabled={savingEnrichment || reimportingEnrichment}
          >
            Cancel
          </Button>
          <Button
            color="primary"
            onClick={saveEnrichedCigar}
            disabled={savingEnrichment || reimportingEnrichment}
          >
            {savingEnrichment ? (
              <>
                <Spinner size="sm" /> Adding…
              </>
            ) : (
              'Add to catalog'
            )}
          </Button>
        </div>
      </Modal>
    </>
  );
}

export default MatchFlowModal;
